import { isEqual } from 'lodash'
import Vue from 'vue'

/**
 * This function tries to solve a Vue limitation, where a computed property (or vuex getter) is
 * invalidated if a dependency is invalidated, even if the computed property (or vuex getter)
 * returns the same value as before.
 *
 * The returned function works like a computed property, but with parameters / result memoize.
 * It will be reactive: every time the `computeValue` function computes a different value than the
 * previous one, the dependencies will be recomputed.
 *
 * @param {Function} prepareParameters Should be using reactive properties to format an object used
 * as an argument for the second function.
 *
 * @param {Function} computeValue Will be called with the first function returned value as argument,
 * and should return the 'final value'.
 *
 * @returns {Function} Will return the 'final value', computed by the `computeValue` argument.
 */
export function createMemoizedGetter(prepareParameters, computeValue) {
  let vm
  let isInitialized = false

  return () => {
    // On the first call, initialize the getter
    if (!isInitialized) {
      // Create a new vm instance to store our reactive value
      vm = new Vue({
        data: { value: null },
      })

      // Watch the prepareParameters function (which should use reactive values) to compute a *non*
      // reactive set of parameters
      vm.$watch(
        prepareParameters,
        // Every time the reactive values used by previousParameters changes, this function will be
        // called
        (parameters, previousParameters) => {
          // Compare the new parameters with the previous ones.  If they did not change, ignore.
          if (isInitialized && isEqual(previousParameters, parameters)) return

          // Compute the new value for this set of parameters
          const newValue = computeValue(parameters)

          // Compare the new value with the previous one.  If it did not change, ignore.
          if (isInitialized && isEqual(vm.value, newValue)) return

          // Store the new value into our reactive property.  This will recompute any dependeant
          // reactive values.
          vm.value = newValue

          isInitialized = true
        },
        { immediate: true },
      )
    }

    // Return our reactive property, which should be initialized at this time.
    return vm.value
  }
}
